// src/components/ui/ConfirmModal.tsx
// Glassmorphic confirmation modal replacing native browser alerts & confirm dialogs.
import ReactDOM from 'react-dom'
import { AlertTriangle, Info, Trash2, X } from 'lucide-react'

export interface ConfirmModalProps {
  isOpen: boolean
  title: string
  description: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info'
  isLoading?: boolean
  onConfirm: () => void | Promise<void>
  onClose: () => void
}

export default function ConfirmModal({
  isOpen,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  if (!isOpen) return null

  const Icon = variant === 'danger' ? Trash2 : variant === 'warning' ? AlertTriangle : Info

  const variantIconStyle =
    variant === 'danger'
      ? 'bg-[var(--rose)]/15 text-[var(--rose)] border-[var(--rose)]/30'
      : variant === 'warning'
      ? 'bg-[#ffb547]/15 text-[#ffb547] border-[#ffb547]/30'
      : 'bg-[var(--mint-500)]/15 text-[var(--mint-500)] border-[var(--mint-500)]/30'

  const confirmBtnStyle =
    variant === 'danger'
      ? 'bg-[var(--rose)] hover:brightness-110 text-white shadow-md'
      : variant === 'warning'
      ? 'bg-[#ffb547] hover:brightness-110 text-black shadow-md'
      : 'bg-gradient-to-r from-[#48d6a2] to-[#22b083] text-[#06231a] shadow-md'

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        className="glass w-full max-w-md rounded-2xl border border-[var(--glass-brd)] bg-[var(--bg-1)] shadow-2xl p-6 relative animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-4 right-4 p-1.5 rounded-xl text-[var(--txt-3)] hover:text-[var(--txt)] hover:bg-[var(--glass-brd)] transition-colors cursor-pointer"
        >
          <X size={18} />
        </button>

        <div className="flex flex-col items-center text-center space-y-4 pt-1">
          {/* Variant Icon */}
          <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center shrink-0 ${variantIconStyle}`}>
            <Icon size={22} className="stroke-[2.2]" />
          </div>

          {/* Title & Description */}
          <div>
            <h3 className="text-base font-extrabold text-[var(--txt)] leading-snug">{title}</h3>
            <p className="text-xs text-[var(--txt-3)] font-medium mt-1.5 leading-relaxed">{description}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 w-full pt-2">
            <button
              type="button"
              disabled={isLoading}
              onClick={onClose}
              className="flex-1 py-2.5 px-4 rounded-xl border border-[var(--glass-brd)] bg-[var(--glass)] text-xs font-extrabold text-[var(--txt-2)] hover:text-[var(--txt)] hover:bg-[var(--glass-brd)] transition-all cursor-pointer disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={async () => {
                await onConfirm()
                onClose()
              }}
              className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-black transition-all cursor-pointer active:scale-95 disabled:opacity-50 ${confirmBtnStyle}`}
            >
              {isLoading ? 'Processing...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
